// Ocean Data Service - Handles data fetching and processing
// In production, this would connect to NOAA, Copernicus Marine Service, or PAGASA APIs

class OceanDataService {
  constructor() {
    this.baseUrl = 'https://api.oceandata.ph'; // Placeholder - would be real API
    this.cache = new Map();
    this.cacheTimeout = 300000; // 5 minutes
  }

  // Philippine coastal cities with coordinates for monitoring
  getPhilippineCities() {
    return [
      {
        id: 'manila-bay',
        name: 'Manila Bay',
        region: 'Metro Manila',
        coordinates: '14.5995°N, 120.9842°E',
        lat: 14.5995,
        lon: 120.9842
      },
      {
        id: 'cebu-city',
        name: 'Cebu City',
        region: 'Central Visayas',
        coordinates: '10.3157°N, 123.8854°E',
        lat: 10.3157,
        lon: 123.8854
      },
      {
        id: 'davao-gulf',
        name: 'Davao Gulf',
        region: 'Davao Region',
        coordinates: '7.1907°N, 125.4553°E',
        lat: 7.1907,
        lon: 125.4553
      },
      {
        id: 'bohol-sea',
        name: 'Bohol Sea',
        region: 'Central Visayas',
        coordinates: '9.6496°N, 124.1434°E',
        lat: 9.6496,
        lon: 124.1434
      },
      {
        id: 'palawan-west',
        name: 'Puerto Princesa',
        region: 'Palawan',
        coordinates: '9.7392°N, 118.7353°E',
        lat: 9.7392,
        lon: 118.7353
      },
      {
        id: 'batangas-bay',
        name: 'Batangas Bay',
        region: 'Calabarzon',
        coordinates: '13.7565°N, 121.0583°E',
        lat: 13.7565,
        lon: 121.0583
      },
      {
        id: 'iloilo-strait',
        name: 'Iloilo Strait',
        region: 'Western Visayas',
        coordinates: '10.7202°N, 122.5621°E',
        lat: 10.7202,
        lon: 122.5621
      },
      {
        id: 'zamboanga-city',
        name: 'Zamboanga City',
        region: 'Zamboanga Peninsula',
        coordinates: '6.9214°N, 122.0790°E',
        lat: 6.9214,
        lon: 122.0790
      },
      {
        id: 'subic-bay',
        name: 'Subic Bay',
        region: 'Central Luzon',
        coordinates: '14.8200°N, 120.2720°E',
        lat: 14.8200,
        lon: 120.2720
      },
      {
        id: 'cagayan-de-oro',
        name: 'Cagayan de Oro',
        region: 'Northern Mindanao',
        coordinates: '8.4542°N, 124.6319°E',
        lat: 8.4542,
        lon: 124.6319
      },
      {
        id: 'legazpi-city',
        name: 'Legazpi City',
        region: 'Bicol Region',
        coordinates: '13.1391°N, 123.7436°E',
        lat: 13.1391,
        lon: 123.7436
      },
      {
        id: 'tacloban-city',
        name: 'Tacloban City',
        region: 'Eastern Visayas',
        coordinates: '11.2421°N, 125.0066°E',
        lat: 11.2421,
        lon: 125.0066
      }
    ];
  }

  // Simulate real-time ocean temperature data
  generateRealisticTemperature(baseTemp, cityId, timestamp) {
    const hour = new Date(timestamp).getHours();
    const dayOfYear = Math.floor((timestamp - new Date(timestamp).getFullYear(), 0, 1) / 86400000);
    
    // Seasonal variation (warmer Apr-Jun, cooler Dec-Feb)
    const seasonalOffset = Math.sin((dayOfYear - 80) * 2 * Math.PI / 365) * 1.5;
    
    // Daily variation (warmer in afternoon)
    const dailyOffset = Math.sin((hour - 6) * Math.PI / 12) * 0.8;
    
    // Location-specific variation
    const locationOffset = this.getLocationOffset(cityId);
    
    // Random variation
    const randomOffset = (Math.random() - 0.5) * 0.5;
    
    return baseTemp + seasonalOffset + dailyOffset + locationOffset + randomOffset;
  }

  getLocationOffset(cityId) {
    const offsets = {
      'manila-bay': 0.5, // Urban heat effect
      'cebu-city': 0.3,
      'davao-gulf': 0.2,
      'bohol-sea': -0.2, // Deeper waters
      'palawan-west': -0.3, // Cleaner waters
      'batangas-bay': 0.4,
      'iloilo-strait': 0.1,
      'zamboanga-city': 0.2,
      'subic-bay': 0.3,
      'cagayan-de-oro': 0.1,
      'legazpi-city': 0.0,
      'tacloban-city': 0.2
    };
    return offsets[cityId] || 0;
  }

  determineAlertLevel(temperature) {
    if (temperature >= 30.5) return 'alert';
    if (temperature >= 29.0) return 'watch';
    return 'normal';
  }

  async getCitiesData() {
    const cities = this.getPhilippineCities();
    const now = Date.now();
    
    return cities.map(city => {
      // Simulate current conditions
      const baseTemp = 28.5; // Average SST for Philippines
      const temperature = this.generateRealisticTemperature(baseTemp, city.id, now);
      const alertLevel = this.determineAlertLevel(temperature);
      
      // Generate trend (simplified)
      const prevTemp = this.generateRealisticTemperature(baseTemp, city.id, now - 3600000);
      const trend = temperature - prevTemp;
      
      return {
        ...city,
        temperature: Math.round(temperature * 10) / 10,
        alertLevel,
        trend: Math.round(trend * 100) / 100,
        waveHeight: Math.random() * 2 + 0.5, // 0.5-2.5m
        lastReading: new Date(now - Math.random() * 1800000), // Within last 30 min
      };
    });
  }

  async getCityDetails(cityId) {
    const cities = await this.getCitiesData();
    return cities.find(city => city.id === cityId);
  }

  async getHistoricalData(cityId, days = 7) {
    const data = [];
    const now = Date.now();
    const baseTemp = 28.5;
    
    for (let i = days; i >= 0; i--) {
      const timestamp = now - (i * 24 * 60 * 60 * 1000);
      const temperature = this.generateRealisticTemperature(baseTemp, cityId, timestamp);
      
      data.push({
        timestamp: new Date(timestamp),
        temperature: Math.round(temperature * 10) / 10,
        alertLevel: this.determineAlertLevel(temperature)
      });
    }
    
    return data;
  }

  async getActiveAlerts() {
    const cities = await this.getCitiesData();
    const alerts = [];
    
    cities.forEach(city => {
      if (city.alertLevel !== 'normal') {
        const duration = Math.floor(Math.random() * 48) + 1; // 1-48 hours
        alerts.push({
          cityName: city.name,
          level: city.alertLevel,
          temperature: city.temperature,
          message: city.alertLevel === 'alert' 
            ? `Critical bleaching risk detected. Temperature has exceeded 30.5°C.`
            : `Elevated temperature detected. Monitor for potential bleaching.`,
          timestamp: new Date(Date.now() - Math.random() * 3600000),
          duration: `${duration} hours`
        });
      }
    });
    
    return alerts.sort((a, b) => {
      if (a.level === 'alert' && b.level !== 'alert') return -1;
      if (b.level === 'alert' && a.level !== 'alert') return 1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });
  }

  // In production, this would fetch from real APIs:
  // - NOAA Coral Reef Watch: https://coralreefwatch.noaa.gov/product/5km/index_5km_sst.php
  // - Copernicus Marine Service: https://marine.copernicus.eu/
  // - PAGASA: https://www.pagasa.dost.gov.ph/
  async fetchRealTimeData(lat, lon) {
    // Placeholder for real API integration
    const mockApiResponse = {
      sst: this.generateRealisticTemperature(28.5, 'default', Date.now()),
      waveHeight: Math.random() * 2 + 0.5,
      timestamp: new Date().toISOString()
    };
    
    return mockApiResponse;
  }
}

export const oceanDataService = new OceanDataService();